import React from 'react';

import { Banner, Box, Button, H1, HStack, Icon, Separator, Text, ToggleTip, VStack } from '@betterment/samba-react';

interface Member {
  id: string;
  commonFirstName: string;
  portfolioControls: {
    securitiesLending: {
      enabled: boolean;
    };
  };
}

interface EditSecuritiesLendingCardProps {
  member: Member;
  handleOpenInviteModal: (memberId: string) => () => void;
  handleOpenUnenrollModal: (memberId: string) => () => void;
  showInviteSentBanner?: boolean;
  showUnenrolledBanner?: boolean;
}

export const EditSecuritiesLendingCard: React.FC<EditSecuritiesLendingCardProps> = ({
  member,
  handleOpenInviteModal,
  handleOpenUnenrollModal,
  showInviteSentBanner = false,
  showUnenrolledBanner = false,
}) => {
  const { id, commonFirstName, portfolioControls } = member;
  return (
    <Box
      key={id}
      css={{
        px: 'space.300',
        pt: 'space.300',
        pb: 'space.100',
        border: '1px solid',
        borderColor: 'border',
        borderRadius: 'large',
      }}
    >
      <VStack>
        <H1 variant="title200" css={{ fontWeight: 'font-weight.semibold', pb: 'space.200' }}>
          {commonFirstName}
        </H1>
        <Separator />
        <HStack css={{ justifyContent: 'space-between', alignItems: 'center', py: 'space.300' }}>
          <VStack>
            <HStack>
              <Text variant="text100" css={{ mr: 'space.50' }}>
                Securities lending
              </Text>
              <ToggleTip>
                <ToggleTip.Trigger iconName="InfoCircle" label="Securities lending" />
                <ToggleTip.Content css={{ maxWidth: '325px' }} align="center" side="top">
                  <Text>
                    Enrolled clients can earn income by lending securities they own without selling or giving up
                    investment exposure. You can sell lent securities or opt clients out of the securities lending
                    program at any time.
                  </Text>
                </ToggleTip.Content>
              </ToggleTip>
            </HStack>
          </VStack>
          <VStack css={{ alignItems: 'end' }}>
            {portfolioControls.securitiesLending.enabled ? (
              <>
                <Box css={{ display: 'flex', alignItems: 'flex-start', gap: 'space.50' }}>
                  <Icon name="CheckmarkCircleFilled" size="small" color="icon.success" />
                  <Text variant="text100" color="text.success">
                    Enabled
                  </Text>
                </Box>

                <Button variant="textPrimary" onClick={handleOpenUnenrollModal(id)}>
                  <Text variant="text50">Turn off</Text>
                </Button>
              </>
            ) : (
              <>
                <Text variant="text100">Disabled</Text>
                <Button variant="textPrimary" onClick={handleOpenInviteModal(id)}>
                  <Text variant="text50">Send invite</Text>
                </Button>
              </>
            )}
          </VStack>
        </HStack>
        {showInviteSentBanner && (
          <Banner variant="success" css={{ mb: 'space.200' }}>
            <Banner.Content>
              <Text variant="text100">
                We sent {commonFirstName} an email inviting them to enable securities lending. It will be turned on once
                they confirm enrollment.
              </Text>
            </Banner.Content>
          </Banner>
        )}
        {showUnenrolledBanner && (
          <Banner variant="success" css={{ mb: 'space.200' }}>
            <Banner.Content>
              <Text variant="text100">Securities lending has been disabled for {commonFirstName}.</Text>
            </Banner.Content>
          </Banner>
        )}
      </VStack>
    </Box>
  );
};
